import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  ExternalEndpointFormErrorsEnum,
  externalEndpointsErrorsLabels,
  validateExternalEndpoint,
} from '../commons/externalEndpointErrors';
import {validateResolvedExpressionRefs} from '../expressions/expressionValidationHelpers';
import {AutolinkerEditorState} from './autolinkerEditorState';

/*
 * Types.
 */

export enum AutolinkerEditorFormErrorsEnum {
  NO_REFERENCE = 'no_reference',
  NO_URL_BUILDER = 'no_url_builder',
  INVALID_EXPRESSIONS = 'invalid_expressions',
  MISSING_EXPRESSION = 'missing_expression',
}

export type AutolinkerEditorFormErrors = AutolinkerEditorFormErrorsEnum | ExternalEndpointFormErrorsEnum;

type AutolinkerChecker = (state: AutolinkerEditorState) => AutolinkerEditorFormErrors | undefined;

/*
 * Checkers.
 */

const ERROR_CHECKS: ReadonlyArray<AutolinkerChecker> = [
  // Reference format.
  ({autolinkerState}) =>
    autolinkerState.referenceFormat.pattern ? undefined : AutolinkerEditorFormErrorsEnum.NO_REFERENCE,

  // URL builder.
  ({autolinkerState, expressions}) => {
    const {urlBuilder} = autolinkerState;

    if (!urlBuilder) {
      return AutolinkerEditorFormErrorsEnum.NO_URL_BUILDER;
    }

    if (!validateResolvedExpressionRefs(urlBuilder, expressions)) {
      return AutolinkerEditorFormErrorsEnum.MISSING_EXPRESSION;
    }

    return validateExternalEndpoint(urlBuilder);
  },

  // Expressions
  ({expressions}) => {
    const areAllAtomsValid = expressions.every((expression) =>
      expression.atoms.every((atom) => atom.isValid),
    );

    return areAllAtomsValid ? undefined : AutolinkerEditorFormErrorsEnum.INVALID_EXPRESSIONS;
  },
];

/*
 * Helpers.
 */

export function computeAutolinkerEditorFormErrors(
  state: AutolinkerEditorState,
): ReadonlyArray<AutolinkerEditorFormErrors> {
  return ERROR_CHECKS.map((checker) => checker(state)).filter(isDefined);
}

/*
 * Labels.
 */

export const autolinkerEditorFormErrorLabels: Record<AutolinkerEditorFormErrors, ReactNode> = {
  ...externalEndpointsErrorsLabels,

  [AutolinkerEditorFormErrorsEnum.NO_REFERENCE]: (
    <Trans id="autolinker-editor-footer-tooltip-warning-no-reference">
      Your autolinker needs to define a reference pattern
    </Trans>
  ),

  [AutolinkerEditorFormErrorsEnum.NO_URL_BUILDER]: (
    <Trans id="autolinker-editor-footer-tooltip-warning-no-url-builder">
      Your autolinker needs to link to a valid HTTPS URL
    </Trans>
  ),

  [AutolinkerEditorFormErrorsEnum.INVALID_EXPRESSIONS]: (
    <Trans id="autolinker-editor-footer-tooltip-warning-invalid-expressions">
      Some of your dynamic variables are invalid
    </Trans>
  ),

  [AutolinkerEditorFormErrorsEnum.MISSING_EXPRESSION]: (
    <Trans id="autolinker-editor-footer-tooltip-warning-missing-expressions">
      Some of your dynamic variables are missing
    </Trans>
  ),
};
